<template>
  <div class="rotashift-agenda">
    <div v-if="getShiftAsignmentType(shift) === 'holiday'" class="title">
      <font-awesome-icon icon="umbrella-beach" fixed-width class="me-1" />
      <span> On Holiday </span>
    </div>
    <div v-if="getShiftAsignmentType(shift) === 'sick'" class="title">
      <font-awesome-icon icon="temperature-high" fixed-width class="me-1" />
      <span> Off Sick </span>
    </div>
    <div v-if="getShiftAsignmentType(shift) === 'old'" class="title">
      <span>(No longer doing this shift)</span>
    </div>
    <div class="time-wrapper d-flex flex-row flex-wrap">
      <div v-if="location.length > 0" class="location me-1">
        {{ location }}
      </div>
      <div class="time flex-grow-1 text-nowrap">
        <font-awesome-icon :icon="['fas', 'clock']" class="me-1" fixed-width />
        <template v-if="rotaTimeStyle({ type: rotaType }) !== 'shift-based'">
          <span>
            {{
              formatTimeHoursMins(
                getShiftAsignment(shift)?.startDate ?? shift.startDate,
              )
            }}
            -
            {{
              formatTimeHoursMins(
                getShiftAsignment(shift)?.endDate ?? shift.endDate,
              )
            }}
          </span>
        </template>
        <template v-else>
          <span v-if="getShiftSession(shift) == null">
            {{ getShiftSession(shift) }}
            Custom shift ({{
              formatTimeHoursMins(
                getShiftAsignment(shift)?.startDate ?? shift.startDate,
              )
            }}
            -
            {{
              formatTimeHoursMins(
                getShiftAsignment(shift)?.endDate ?? shift.endDate,
              )
            }})
          </span>
          <span v-else>
            {{ getShiftSession(shift).name }} shift ({{
              formatTimeHoursMins(
                getShiftAsignment(shift)?.startDate ?? shift.startDate,
              )
            }}
            -
            {{
              formatTimeHoursMins(
                getShiftAsignment(shift)?.endDate ?? shift.endDate,
              )
            }})
          </span>
        </template>
      </div>
      <BDropdown
        v-if="canShowContextMenu"
        :id="`shift-${shift.id}-actions-dropdown`"
        class="shift-actions"
        toggle-class="py-0"
        menu-class=""
        variant="link"
        size="sm"
        :teleport-disabled="true"
        no-caret
        aria-label="shift actions"
      >
        <template #button-content>
          <font-awesome-icon icon="people-arrows" fixed-width class="me-1" />
          <font-awesome-icon
            icon="ellipsis-vertical"
            alt="Open Shift Actions"
            title="Open Shift Actions"
          />
        </template>
        <BDropdownItemButton
          v-if="canShowCoverRequestButton"
          @click.prevent="
            $event.stopPropagation();
            requestCover(shift, rotaId);
          "
        >
          <font-awesome-icon icon="people-arrows" fixed-width class="me-1" />
          Request cover for this shift
        </BDropdownItemButton>
      </BDropdown>
      <button
        v-if="canShowCoverCancelButton"
        class="shift-actions btn btn-sm btn-outline-danger"
        @click.prevent="
          $event.stopPropagation();
          cancelRequestCover(shift, rotaId);
        "
      >
        <font-awesome-icon icon="ban" fixed-width class="me-1" />
        Cancel the cover request for this shift
      </button>
      <button
        v-if="canShowShiftRequestCancelButton"
        class="shift-actions btn btn-sm btn-outline-danger"
        @click.prevent="
          $event.stopPropagation();
          cancelVolunteer(shift, rotaId);
        "
      >
        <font-awesome-icon icon="ban" fixed-width class="me-1" />
        Cancel shift request
      </button>
    </div>
    <div
      v-if="shiftCurrentAssignmentForUser.selfAssigned"
      class="self-assigned"
    >
      <font-awesome-icon fixed-width :icon="['far', 'hand-paper']" />
      <span>
        This shift was self-assigned on
        {{ formatModifiedDateLong(shiftCurrentAssignmentForUser.selfAssigned) }}
      </span>
    </div>
    <button
      v-if="
        getShiftAsignmentType(shift) !== 'current' &&
        hasAssignments &&
        !alwaysExpanded
      "
      class="btn btn-sm btn-outline-primary"
      type="button"
      @click="expanded = !expanded"
    >
      <span v-if="expanded">
        <font-awesome-icon icon="angle-up" fixed-width /> Hide Details
      </span>
      <span v-else>
        <font-awesome-icon icon="angle-down" fixed-width /> Show Details
      </span>
    </button>
    <div
      v-if="getShiftDuties.length > 0 && (expanded || alwaysExpanded)"
      class="duties"
    >
      Duties:
      <span v-for="(role, index) in getShiftDuties" :key="index">
        <span v-if="index > 0">, </span>
        {{ role.name }}
      </span>
    </div>
    <div v-if="shift.notes && (expanded || alwaysExpanded)" class="mb-2 notes">
      {{ shift.notes }}
    </div>
    <div
      v-if="(showAssignedTo && expanded) || (alwaysExpanded && !hideAssigned)"
      class="mb-2 assigned-to"
    >
      <div>Assigned to</div>
      <div>
        <rota-shift-assignedto-view-for-agenda
          :user-id="userId"
          :shift-id="shift.id"
          :assigned-to="shift.assignedTo ?? []"
        />
      </div>
    </div>
    <div class="d-flex flex-column activities">
      <!-- rota shift schedule and activities need to be at the same DOM level for css ordering to work -->
      <rota-agenda-shift-schedule-view
        v-if="shift.schedule && shift.schedule.length > 0"
        :shift="shift"
      />
      <rota-agenda-shift-activities :shift="shift" :activities="activities" />
    </div>
  </div>
</template>

<script>
import { computed, toRaw } from "vue";
import { mapGetters } from "vuex";
import find from "lodash/find";
import findLast from "lodash/findLast";
import has from "lodash/has";
import { Hub } from "aws-amplify/utils";
import { useConfirm } from "primevue/useconfirm";
import { useRotaStore } from "../../../store/rotas";
import { getShiftSession } from "../../../lib/rota/shift-functions";
import {
  formatDateAsDay,
  formatRotaStartDateLong,
  formatTimeHoursMins,
  formatModifiedDateLong,
  rotaTimeStyle,
  formatTimeStringForHtmlInput,
} from "../../../lib/date-functions";
import sessions from "../../../lib/rota/sessions";
import {
  SHIFTCOVER_STATUS_APPROVED,
  SHIFTCOVER_STATUS_VOLUNTEERED,
  SHIFTCOVER_STATUS_PENDING,
  SHIFTCOVER_STATUS_OPEN,
  SHIFTCOVER_STATUS_REJECTED,
} from "../../../lib/constants/shift-cover-requests";
import RotaShiftAssignedtoViewForAgenda from "./RotaShiftAssignedtoViewForAgenda.vue";
import RotaAgendaShiftActivities from "./RotaAgendaShiftActivities.vue";
import RotaAgendaShiftScheduleView from "./RotaAgendaShiftScheduleView.vue";
import useShiftCoverRequestSequence from "@/components/form/sequences/useShiftCoverRequestSequence";
import useFormSequenceModal from "@/components/form/useFormSequenceModal";
import { isFeatureEnabled, FLAG_2393_SHIFT_COVER } from "@/flags.js";

export default {
  name: "RotaAgendaShiftView",
  components: {
    RotaShiftAssignedtoViewForAgenda,
    RotaAgendaShiftActivities,
    RotaAgendaShiftScheduleView,
  },
  props: {
    userId: {
      type: String,
      default() {
        return null;
      },
    },
    rotaType: {
      type: String,
      default() {
        return "";
      },
    },
    rotaId: {
      type: String,
      default() {
        return "";
      },
    },
    rotaStatus: {
      type: String,
      default() {
        return "";
      },
    },
    shift: {
      type: Object,
      default() {
        return {};
      },
    },
    rotaAgencies: {
      type: Array,
      default() {
        return [];
      },
    },
    activities: {
      type: [Boolean, Array],
      default() {
        return [];
      },
    },
    alwaysExpanded: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideAssigned: {
      type: Boolean,
      default() {
        return false;
      },
    },
    location: {
      type: String,
      default() {
        return "";
      },
    },
  },
  setup(props) {
    const { modalVisible: formSequenceModalVisible, showFormSequenceModal } =
      useFormSequenceModal();

    const confirm = useConfirm();

    const shiftCurrentAssignmentForUser = computed(() => {
      if (!has(props.shift, "assignedTo")) {
        return {};
      }
      const lastEntry = findLast(props.shift.assignedTo, {
        userId: props.userId,
        type: "current",
      });
      return lastEntry === undefined ? {} : lastEntry;
    });

    const canShowCoverRequestButton = computed(() => {
      if (!isFeatureEnabled(FLAG_2393_SHIFT_COVER)) {
        return false;
      }
      if (shiftCurrentAssignmentForUser.value.userId !== props.userId) {
        return false;
      }
      if (props.shift.coverRequestStatus !== undefined) {
        return false;
      }
      if (props.shift.startDate < new Date()) {
        return false;
      }
      return true;
    });
    const canShowCoverCancelButton = computed(() => {
      if (shiftCurrentAssignmentForUser.value.userId !== props.userId) {
        return false;
      }
      if (!props.shift.coverRequest) {
        return false;
      }
      if (props.shift.coverRequestStatus === SHIFTCOVER_STATUS_REJECTED) {
        return false;
      }
      if (props.shift.coverRequestStatus === SHIFTCOVER_STATUS_APPROVED) {
        return false;
      }
      return true;
    });
    const canShowShiftRequestCancelButton = computed(() => {
      if (shiftCurrentAssignmentForUser.value.userId === props.userId) {
        return false;
      }
      if (props.shift.coverRequestStatus === SHIFTCOVER_STATUS_REJECTED) {
        return false;
      }
      if (props.shift.coverRequestStatus === SHIFTCOVER_STATUS_APPROVED) {
        return false;
      }

      const volunteered = findLast(props.shift.coverRequest, {
        userId: props.userId,
        status: SHIFTCOVER_STATUS_VOLUNTEERED,
      });

      if (volunteered) {
        return true;
      }
      return false;
    });
    const canShowContextMenu = computed(() => {
      return canShowCoverRequestButton.value;
    });

    const requestCover = (shift) => {
      showFormSequenceModal(
        "Shift Cover Request",
        "people-arrows",
        useShiftCoverRequestSequence,
        {
          shift: toRaw(shift),
          activities: toRaw(props.activities) || [],
        },
        onRequestCoverSubmit,
      );
    };
    const cancelRequestCover = (shift) => {
      confirm.require({
        message:
          "Are you sure you want to cancel this cover request? You won't be able to re-open it later!",
        header: "Confirmation",
        acceptLabel: "Yes, cancel it",
        accept: () => {
          const rotaStore = useRotaStore();
          rotaStore
            .rejectCoverRequest(props.rotaId, shift.id)
            .then((result) => {
              if (result !== false) {
                Hub.dispatch("shift", {
                  event: "updated",
                  data: result,
                });
                Hub.dispatch("toast", {
                  event: "success",
                  message: "Cover request cancelled",
                  data: {
                    timeout: 3000,
                  },
                });
              }
            });
        },
      });
    };

    const cancelVolunteer = (shift) => {
      confirm.require({
        message: "Are you sure you no longer want to volunteer for this shift?",
        header: "Confirmation",
        acceptLabel: "Yes, I am sure",
        accept: () => {
          const rotaStore = useRotaStore();
          rotaStore
            .rejectCoverRequest(props.rotaId, shift.id)
            .then((result) => {
              if (result !== false) {
                Hub.dispatch("shift", {
                  event: "updated",
                  data: result,
                });
                Hub.dispatch("toast", {
                  event: "success",
                  message: "You are no longer volunteering for this shift",
                  data: {
                    timeout: 3000,
                  },
                });
              }
            });
        },
      });
    };

    const onRequestCoverSubmit = async (data) => {
      const rotaStore = useRotaStore();
      const result = await rotaStore.submitCoverRequest(
        props.rotaId,
        data.shiftId,
        data.body,
      );

      if (result !== false) {
        Hub.dispatch("shift", {
          event: "updated",
          data: result,
        });

        switch (result.coverRequestStatus) {
          case SHIFTCOVER_STATUS_PENDING:
            Hub.dispatch("toast", {
              event: "warning",
              message: "Cover request submitted. Waiting for approval.",
              data: {
                timeout: 3000,
              },
            });
            break;
          case SHIFTCOVER_STATUS_OPEN:
            Hub.dispatch("toast", {
              event: "success",
              message:
                "Cover request is approved, staff can now volunteer to take on this shift.",
              data: {
                timeout: 3000,
              },
            });
            break;
          default:
            Hub.dispatch("toast", {
              event: "info",
              message: `Cover request submitted : ${result.coverRequestStatus}`,
              data: {
                timeout: 3000,
              },
            });
            break;
        }

        formSequenceModalVisible.value = false;
        return true;
      }
      return false;
    };

    return {
      findLast,
      sessions,
      getShiftSession,
      rotaTimeStyle,
      formatDateAsDay,
      formatRotaStartDateLong,
      formatTimeHoursMins,
      formatModifiedDateLong,
      formatTimeStringForHtmlInput,
      shiftCurrentAssignmentForUser,
      canShowContextMenu,
      canShowCoverRequestButton,
      canShowCoverCancelButton,
      canShowShiftRequestCancelButton,
      requestCover,
      cancelRequestCover,
      cancelVolunteer,
    };
  },
  data() {
    return {
      expanded: true,
      showUsers: false,
    };
  },
  computed: {
    ...mapGetters({
      ccLocations: "locations",
      dutiesQualifiedFor: "dutiesQualifiedFor",
    }),
    hasAssignments() {
      return has(this.shift, "assignedTo");
    },
    getShiftDate() {
      if (this.rotaStatus === "master") {
        return `${formatDateAsDay(this.shift.startDate)}`;
      }
      return `${formatRotaStartDateLong(this.shift.startDate)}`;
    },
    getShiftDuties() {
      const duties = [];
      if (this.shift.dutiesRequired) {
        this.shift.dutiesRequired.forEach((duty) => {
          const dutyObj = find(this.dutiesQualifiedFor, { value: duty });
          if (dutyObj !== undefined) {
            duties.push(dutyObj);
          }
        });
      }
      return duties;
    },
    showAssignedTo() {
      if (this.hideAssigned) {
        return false;
      }
      if (!has(this.shift, "assignedTo")) {
        return false;
      }
      if (
        this.shift.assignedTo.length === 1 &&
        this.shift.assignedTo[0].userId === this.userId
      ) {
        return false;
      }
      return true;
    },
  },
  watch: {
    shift: {
      handler(newData) {
        this.expanded = this.getShiftAsignmentType(newData) === "current";
        if (!has(this.shift, "assignedTo")) {
          this.expanded = true;
        }
        if (this.alwaysExpanded) {
          this.expanded = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getShiftAsignmentType(shift) {
      if (!has(shift, "assignedTo")) {
        return "";
      }
      const match = findLast(shift.assignedTo, { userId: this.userId });
      return match ? match.type : "";
    },
    getShiftAsignment(shift) {
      if (!has(shift, "assignedTo")) {
        return "";
      }
      return findLast(shift.assignedTo, { userId: this.userId });
    },
  },
};
</script>

<!--
  note, don't put any CSS in here as it causes
  mini-css-extract-plugin warnings on production build
-->
